import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import dayjs from "dayjs";
import { loadLogoAsBase64 } from "./logoLoader";
import { STATUS_OPTIONS_PRIVATE } from "../constants/status";
import { TYPE_OPTIONS } from "../constants/agreementType";
import { getAiReport } from "../services/aiReportService";
import showdown from "showdown";
import htmlToPdfmake from "html-to-pdfmake";

pdfMake.vfs = pdfFonts.default;

// 🔹 Función para calcular estado de vigencia
function getAgreementValidityStatus(agreement) {
  const allowedStatuses = [
    "signed",
    "next_to_expired",
    "expired",
    "finished",
  ];

  if (!allowedStatuses.includes(agreement.status)) {
    return { label: "—", color: "black" }; // No aplica
  }

  const today = dayjs();
  const effectiveDate = agreement.effective_date
    ? dayjs(agreement.effective_date)
    : null;

  if (!effectiveDate) return { label: "—", color: "black" };

  if (agreement.status === "finished") return { label: "Terminado", color: "green" };
  if (agreement.status === "expired") return { label: "Vencido", color: "black" };
  if (agreement.status === "next_to_expired") return { label: "Por vencer", color: "orange" };

  if (today.isAfter(effectiveDate)) return { label: "Vencido", color: "red" };
  if (today.isBefore(effectiveDate) && today.add(30, "day").isAfter(effectiveDate))
    return { label: "Por vencer", color: "orange" };

  return { label: "Vigente", color: "green" };
}

export async function generatePublicAgreementsToPdf(agreements, filters = {}, useAI = false, customPrompt = "") {
  const logoBase64 = await loadLogoAsBase64();
  let aiReportContent = null;
  let aiReport = null; // Declaramos la variable antes de usarla
  if (useAI) {
    aiReport = await getAiReport(agreements, customPrompt);

    // Convertimos Markdown → HTML → pdfMake
    const converter = new showdown.Converter();
    const html = converter.makeHtml(aiReport || "No se pudo generar el informe IA");
    aiReportContent = htmlToPdfmake(html, { window: window });
  }



  // Diccionario para mostrar etiquetas en español
  const FILTER_LABELS = {
    q: "Búsqueda",
    status: "Estado",
    type: "Tipo",
    subscription_from: "Desde fecha de suscripción",
    subscription_to: "Hasta fecha de suscripción",
    validity_from: "Desde fecha de vigencia",
    validity_to: "Hasta fecha de vigencia",
  };

  // Construcción del texto de filtros aplicados
  const filtrosAplicados = Object.entries(filters)
    .filter(([_, v]) => v !== "" && v !== null && v !== "all")
    .map(([k, v]) => {
      const label = FILTER_LABELS[k] || k;

      // Buscar traducción en opciones
      let valueLabel = v;
      if (k === "status") {
        const found = STATUS_OPTIONS_PRIVATE.find((s) => s.value === v);
        valueLabel = found ? found.label : v;
      }
      if (k === "type") {
        const found = TYPE_OPTIONS.find((t) => t.value === v);
        valueLabel = found ? found.label : v;
      }

      return `• ${label}: ${valueLabel}`;
    })
    .join("\n");

  // 🔹 Construcción de tabla
  const tableBody = [
    [
      { text: "N° de orden", style: "tableHeader", alignment: "center" },
      { text: "Tipo", style: "tableHeader", alignment: "center" },
      { text: "Objetivo", style: "tableHeader", alignment: "center" },
      { text: "Institución", style: "tableHeader", alignment: "center" },
      { text: "Fecha de suscripción", style: "tableHeader", alignment: "center" },
      { text: "Fecha de vigencia", style: "tableHeader", alignment: "center" },
      { text: "Estado Vigencia", style: "tableHeader", alignment: "center" },
    ],
    ...agreements.map((a) => {
      const vigencia = getAgreementValidityStatus(a);

      return [
        { text: a.order_number, alignment: "center" },
        { text: a.agreement_type, alignment: "center" },
        { text: a.objective || "—", alignment: "justify" },
        { text: a.entity || "—", alignment: "justify" },
        {
          text: a.suscription_date
            ? dayjs(a.suscription_date).format("DD/MM/YYYY")
            : "—",
          alignment: "center",
        },
        {
          text: a.effective_date
            ? dayjs(a.effective_date).format("DD/MM/YYYY")
            : "—",
          alignment: "center",
        },
        {
          text: vigencia.label,
          alignment: "center",
          color: vigencia.color,
        },
      ];
    }),
  ];

  const docDefinition = {
    pageSize: "A4",
    pageOrientation: "landscape",
    pageMargins: [30, 90, 30, 40],

    header: () => ({
      margin: [30, 20, 30, 10],
      table: {
        widths: [60, "*"],
        body: [
          [
            {
              image: logoBase64,
              width: 50,
              rowSpan: 2,
              alignment: "left",
              margin: [0, 0, 10, 0],
            },
            {
              text: "UNIVERSIDAD NACIONAL DE ITAPÚA",
              style: "universityName",
              alignment: "center",
            },
          ],
          [
            {},
            {
              text: "Reporte de Convenios",
              style: "headerText",
              alignment: "center",
              margin: [0, 2, 0, 0],
            },
          ],
        ],
      },
      layout: "noBorders",
    }),

    footer: (currentPage, pageCount) => ({
      columns: [
        { text: "Departamento de Asuntos Jurídicos - UNI", alignment: "left", fontSize: 8, margin: [30, 0, 0, 0] },
        { text: `Página ${currentPage} de ${pageCount}`, alignment: "right", fontSize: 9, margin: [0, 0, 30, 0] },
      ],
    }),

    content: [
      filtrosAplicados
        ? {
            text: `Filtros aplicados:\n${filtrosAplicados}`,
            fontSize: 9,
            margin: [0, 0, 0, 8],
          }
        : null,
      useAI
        ? {
            stack: [
              {
                text: "Informe",
                style: "subheader",
                margin: [0, 10, 0, 4],
              },
              {
                stack: aiReportContent, // ya convertido en pdfMake
                fontSize: 10,
                italics: true,
                margin: [0, 0, 0, 12],
              },
            ],
          }
        : null,
      {
        text: `Generado el ${dayjs().format("DD/MM/YYYY")}`,
        fontSize: 9,
        alignment: "right",
        margin: [0, 0, 0, 8],
      },
      {
        text: `Total de convenios: ${agreements.length}`,
        alignment: "right",
        margin: [0, 0, 0, 12],
        bold: true,
        fontSize: 10,
      },
      {
        table: {
          headerRows: 1,
          widths: ["auto", "auto", "*", "*", "auto", "auto", "auto"],
          body: tableBody,
        },
        layout: {
          fillColor: (rowIndex) => {
            if (rowIndex === 0) return "#eeeeee"; // Header
            return rowIndex % 2 === 0 ? "#f1f7ff" : null; // Zebra striping
          },
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => "#ccc",
          vLineColor: () => "#ccc",
        },
      },
    ].filter(Boolean),

    styles: {
      universityName: { fontSize: 16, bold: true },
      headerText: { fontSize: 12, bold: true },
      subheader: { fontSize: 12, bold: true, decoration: "underline" },
      tableHeader: { bold: true, fillColor: "#eeeeee", fontSize: 11 },
    },
  };

  pdfMake.createPdf(docDefinition).download("convenios.pdf");
}
